import { useFetch } from "hooks/api";
import { useMemo } from "react";
import moment from "moment";
import { trellisPalette } from "components/custom/analytics/palettes";
import { useDates } from "../../../dates/DatesContext";
import AnnotationChartTemplate from "../growth/AnnotationChartTemplate";

const groupToLegacyGroup = {
  day: "daily",
  week: "weekly",
  month: "monthly",
};

const AnalyticsOverviewChart = () => {
  const { group, start, end } = useDates();

  const shelfStatsQuery = useFetch(
    ["shelf_timeline"],
    "/api/shelf_stats",
    {
      stats_by: "time_series",
      end: end.toISOString(),
      start: start.toISOString(),
      period: groupToLegacyGroup[group],
    },
    {
      select: (data) => data.data,
      gcTime: 0,
      staleTime: 0,
    },
  );

  const atfData = useMemo(() => {
    return shelfStatsQuery.data?.filter((item) => {
      if (item.rollup_type === "above_fold") {
        const newRow = item;
        item.mDate = moment(item.date);
        return newRow;
      }
      return false;
    });
  }, [shelfStatsQuery.data]);

  const chartData = shelfStatsQuery.data?.filter((item) => {
    if (item.rollup_type === "page_1") {
      const newRow = item;
      item.mDate = moment(item.date);
      return newRow;
    }
    return false;
  });

  const options = useMemo(() => {
    let chartDates = [];
    if (group === "month") {
      chartDates = chartData?.map((d) => d.mDate?.endOf("M").format("YYYY-MM-DD")) ?? [];
    } else if (group === "week") {
      chartDates = chartData?.map((d) => d.mDate?.endOf("W").format("YYYY-MM-DD")) ?? [];
    } else if (group === "day") {
      chartDates = chartData?.map((d) => d.mDate?.format("YYYY-MM-DD")) ?? [];
    }

    return {
      legend: {
        align: "center",
        verticalAlign: "bottom",
        x: 0,
        y: 0,
      },
      title: {
        text: null,
      },
      // This is blank to overwrite default options for performance chart
      plotOptions: {},
      xAxis: {
        categories: chartDates?.slice(Math.max(chartData?.length - 30, 0)),
        labels: {
          step: group === "day" ? 5 : 1,
        },
      },
      yAxis: [
        {
          title: {
            text: "Share",
          },
          labels: {
            format: "{value}%",
          },
        },
        {
          title: {
            text: "Position",
          },
          opposite: true,
        },
      ],
      tooltip: {
        shared: true,
        pointFormat: "{series.name}: <b>{point.y}</b><br/>",
      },
      series: [
        {
          color: trellisPalette[3],
          type: "column",
          name: "Average Position",
          data: chartData?.map((d) => d.position_avg)?.slice(Math.max(chartData?.length - 30, 0)),
          yAxis: 1,
        },
        {
          color: trellisPalette[0],
          type: "line",
          name: `SOS`,
          data: chartData?.map((d) => d.share_of_shelf)?.slice(Math.max(chartData?.length - 30, 0)),
          yAxis: 0,
        },
        {
          color: trellisPalette[0],
          type: "line",
          name: `SOS Organic`,
          data: chartData?.map((d) => d.org_share_of_shelf)?.slice(Math.max(chartData?.length - 30, 0)),
          yAxis: 0,
        },
        {
          color: trellisPalette[2],
          type: "line",
          name: "SOV",
          data: chartData?.map((d) => d.share_of_voice)?.slice(Math.max(chartData?.length - 30, 0)),
          yAxis: 0,
        },
        {
          color: trellisPalette[1],
          type: "line",
          name: `ATF SOS`,
          data: atfData?.map((d) => d.share_of_shelf)?.slice(Math.max(chartData?.length - 30, 0)),
          yAxis: 0,
        },
        {
          color: trellisPalette[1],
          type: "line",
          name: `ATF SOS Organic`,
          data: atfData?.map((d) => d.org_share_of_shelf)?.slice(Math.max(chartData?.length - 30, 0)),
          yAxis: 0,
        },
        {
          color: trellisPalette[4],
          type: "line",
          name: "ATF SOV",
          data: atfData?.map((d) => d.share_of_voice)?.slice(Math.max(chartData?.length - 30, 0)),
          yAxis: 0,
        },
      ],
    };
  }, [chartData, group, atfData]);

  return (
    <AnnotationChartTemplate
      options={options}
      group={group}
      yLabels={true}
      height={400}
      isChartDataLoading={shelfStatsQuery.isLoading}
    />
  );
};

export default AnalyticsOverviewChart;
